/**
 * RBAC API client for fetching user permissions from /api/rbac/me
 *
 * This module provides type-safe access to the RBAC endpoint
 * with proper error handling for different failure scenarios.
 */

import type { StudioMembership } from '@/types/permissions'

/**
 * Response structure from /api/rbac/me endpoint
 */
export interface RbacMeResponse {
  data: RbacMeData
  meta: {
    timestamp: number
    signature: string
  }
}

/**
 * RBAC data returned from the API
 */
export interface RbacMeData {
  userId: string
  studioId: string
  roles: string[]
  permissions: string[]
  membership: StudioMembership
}

/**
 * Error response structure from the API
 */
export interface RbacErrorResponse {
  error: string
  code: RbacErrorCode
}

/**
 * Error codes returned by the RBAC API
 */
export type RbacErrorCode =
  | 'MISSING_TOKEN'
  | 'INVALID_FORMAT'
  | 'EXPIRED'
  | 'INVALID_SIGNATURE'
  | 'INVALID_ISSUER'
  | 'INVALID_AUDIENCE'
  | 'INVALID_CLAIMS'
  | 'JWKS_ERROR'
  | 'INTERNAL_ERROR'
  | 'CONFIG_ERROR'

/**
 * Custom error class for RBAC API errors
 */
export class RbacApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: RbacErrorCode,
    message: string
  ) {
    super(message)
    this.name = 'RbacApiError'
  }

  /**
   * Check if this error indicates an expired or invalid token
   * that requires re-authentication
   */
  get requiresReauth(): boolean {
    return (
      this.status === 401 &&
      ['EXPIRED', 'INVALID_SIGNATURE', 'INVALID_CLAIMS'].includes(this.code)
    )
  }

  /**
   * Check if this error is a temporary failure that can be retried
   */
  get isRetryable(): boolean {
    return this.status >= 500 || this.code === 'JWKS_ERROR'
  }
}

/**
 * RBAC API client
 */
export const rbacApi = {
  /**
   * Fetch the current user's RBAC data
   *
   * @param token - JWT access token for authentication
   * @param studioId - Optional studio ID to scope the request
   * @returns RBAC response with user permissions
   * @throws RbacApiError on API errors
   */
  async getMe(token: string, studioId?: string): Promise<RbacMeResponse> {
    const url = new URL('/api/rbac/me', window.location.origin)
    if (studioId) {
      url.searchParams.set('studioId', studioId)
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      let errorData: RbacErrorResponse
      try {
        errorData = await response.json()
      } catch {
        throw new RbacApiError(
          response.status,
          'INTERNAL_ERROR',
          `RBAC API returned ${response.status}`
        )
      }

      throw new RbacApiError(
        response.status,
        errorData.code,
        errorData.error
      )
    }

    return response.json()
  },
}
